import dayjs from 'dayjs';
import * as z from 'zod';

export enum CertificationScheme {
  CLASSNKCORSIA = 'classNkCorsia',
  RSBCORSIA = 'rsbCorsia',
  ISCCCORSIA = 'isccCorsia',
  RSBEURED = 'rsbEuRed',
  ISCCEU = 'isccEu',
  RSBGlobal = 'rsbGlobal',
  ISCCPLUS = 'isccPlus',
}

export const CertificationSchemeOther = 'other' as const;
export type CertificationSchemeOther = typeof CertificationSchemeOther;

export const ZodCertificationScheme = z.enum(CertificationScheme);
export const ZodAdditionalCertificationScheme = z.enum(CertificationScheme).or(z.literal(CertificationSchemeOther));

export enum CertificationType {
  Default = 'default',
  Actual = 'actual',
}

export const ZodCertificationType = z.enum(CertificationType);

export type CertificationSchemeCorsia = CertificationScheme.RSBCORSIA | CertificationScheme.ISCCCORSIA | CertificationScheme.CLASSNKCORSIA;
export const CertificationSchemeCorsia = [CertificationScheme.RSBCORSIA, CertificationScheme.ISCCCORSIA, CertificationScheme.CLASSNKCORSIA];
export type CertificationSchemeEU = CertificationScheme.RSBEURED | CertificationScheme.ISCCEU | CertificationScheme.RSBGlobal | CertificationScheme.ISCCPLUS;
export const CertificationSchemeEU = [CertificationScheme.RSBEURED, CertificationScheme.ISCCEU, CertificationScheme.RSBGlobal, CertificationScheme.ISCCPLUS];

export const ZodCertificationSchemeCorsia = z.enum([CertificationScheme.RSBCORSIA, CertificationScheme.ISCCCORSIA, CertificationScheme.CLASSNKCORSIA]);
export const ZodCertificationSchemeEU = z.enum([CertificationScheme.RSBEURED, CertificationScheme.ISCCEU, CertificationScheme.RSBGlobal, CertificationScheme.ISCCPLUS]);

export const ZodCertificationExpiryDate = z.iso.date().refine((date) => {
  const d = dayjs(date);
  const now = dayjs();
  return d.isSame(now, 'day') || d.isAfter(now, 'day');
}, { error: 'Expiry date cannot be in past' });

export type LowILUCRiskConfirmed = {
  isLowILUCRiskConfirmed: true;
  lowRiskAuditCertificateNumber: string;
  lowRiskAuditExpiryDate: string;
  lowRiskAuditCertificationBody: string;
  lowRiskAuditCertificationBodyEmail: string;
  /** [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing */
  lowRiskFollowUnusedLandApproach: boolean;
};

export type LowILUCRisk
  = | { isLowILUCRiskConfirmed: false }
    | LowILUCRiskConfirmed;

export const ZodLowILUCRisk = z.discriminatedUnion('isLowILUCRiskConfirmed', [
  z.object({ isLowILUCRiskConfirmed: z.literal(false) }),
  z.object({
    isLowILUCRiskConfirmed: z.literal(true),
    lowRiskFollowUnusedLandApproach: z.boolean(),
    lowRiskAuditCertificateNumber: z.string().min(1),
    lowRiskAuditExpiryDate: ZodCertificationExpiryDate,
    lowRiskAuditCertificationBody: z.string().min(1),
    lowRiskAuditCertificationBodyEmail: z.email(),
  }),
]);

export type LowDisplacementRisk
  = | { isLowDisplacementRiskConfirmed: false }
    | { isLowDisplacementRiskConfirmed: true; lowDisplacementRiskAuditCertificateNumber: string; lowDisplacementRiskAuditExpiryDate: string; lowDisplacementRiskAuditCertificationBody: string; lowDisplacementRiskAuditCertificationBodyEmail: string };

export const ZodLowDisplacementRisk = z.discriminatedUnion('isLowDisplacementRiskConfirmed', [
  z.object({ isLowDisplacementRiskConfirmed: z.literal(false) }),
  z.object({
    isLowDisplacementRiskConfirmed: z.literal(true),
    lowDisplacementRiskAuditCertificateNumber: z.string().min(1),
    lowDisplacementRiskAuditExpiryDate: ZodCertificationExpiryDate,
    lowDisplacementRiskAuditCertificationBody: z.string().min(1),
    lowDisplacementRiskAuditCertificationBodyEmail: z.email(),
  }),
]);

export type AdditionalCertification
  = | { additionalScheme: undefined }
    | {
      additionalScheme: CertificationScheme;
      additionalCertificateNumber: string;
      additionalExpiryDate: string;
      additionalCertificationBody: string;
      additionalCertificationBodyEmail: string;
      /**
      * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
      * is set >only< if additionalScheme is ISCCPLUS <- verified through zod, optional here for simplicity of types
      */
      additionalHasIRS7210ForIsccPlus?: boolean;
      /**
      * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
      * is set >only< if additionalScheme is RSBEURED or ISCCEU <- verified through zod, optional here for simplicity of types
      */
      additionalHasSeparateRFNBORCF?: boolean;
    }
    | {
      additionalScheme: typeof CertificationSchemeOther;
      additionalSchemeName: string;
      additionalCertificateNumber: string;
      additionalExpiryDate: string;
      additionalCertificationBody: string;
      additionalCertificationBodyEmail: string;
    };

export const ZodAdditionalCertification = z.discriminatedUnion('additionalScheme', [
  z.object({ additionalScheme: z.literal(undefined).optional() }),
  z.object({
    additionalScheme: ZodCertificationScheme,
    additionalCertificateNumber: z.string().min(1),
    additionalExpiryDate: ZodCertificationExpiryDate,
    additionalCertificationBody: z.string().min(1),
    additionalCertificationBodyEmail: z.email(),
    additionalHasIRS7210ForIsccPlus: z.boolean().optional(),
    additionalHasSeparateRFNBORCF: z.boolean().optional(),
  }).refine((data) => {
    if (data.additionalScheme === CertificationScheme.ISCCPLUS) {
      return data.additionalHasIRS7210ForIsccPlus !== undefined;
    } else {
      return data.additionalHasIRS7210ForIsccPlus === undefined;
    }
  }, { error: 'If and only if additional certification scheme is ISCC PLUS then accompanying IRS form 7210 field is required', path: ['additionalHasIRS7210ForIsccPlus'] })
    .refine((data) => {
      if (data.additionalScheme === CertificationScheme.RSBEURED || data.additionalScheme === CertificationScheme.ISCCEU) {
        return data.additionalHasSeparateRFNBORCF !== undefined;
      } else {
        return data.additionalHasSeparateRFNBORCF === undefined;
      }
    }, { error: 'If and only if additional certification scheme is RSB EU RED or ISCC EU then RFNBO/RCF document field is required', path: ['additionalHasSeparateRFNBORCF'] }),
  z.object({
    additionalScheme: z.literal(CertificationSchemeOther),
    additionalSchemeName: z.string().min(1),
    additionalCertificateNumber: z.string().min(1),
    additionalExpiryDate: ZodCertificationExpiryDate,
    additionalCertificationBody: z.string().min(1),
    additionalCertificationBodyEmail: z.email(),
  }),
]);

export type Certification<
  Scheme extends CertificationScheme = CertificationScheme,
  Type extends CertificationType = CertificationType,
> = {
  scheme: Scheme;
  type: Type;
  certificateNumber: string;
  certificationBody: string;
  expiryDate: string;
  certificationBodyEmail: string;
  traderCertificationNumber: string;
  /**
   * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
   * is set >only< if scheme is ISCCPLUS <- verified through zod, optional here for simplicity of types
   */
  hasIRS7210ForIsccPlus?: boolean;
  /**
    * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
    * is set >only< if additionalScheme is RSBEURED or ISCCEU <- verified through zod, optional here for simplicity of types
    */
  hasSeparateRFNBORCF?: boolean;
} & LowILUCRisk & LowDisplacementRisk & AdditionalCertification;

export const ZodCertification = <Scheme, Type>(scheme: Scheme, type: Type) => z.object({
  scheme,
  type,
  certificateNumber: z.string().min(1),
  certificationBody: z.string().min(1),
  expiryDate: ZodCertificationExpiryDate,
  certificationBodyEmail: z.email(),
  traderCertificationNumber: z.string().min(1),
  hasIRS7210ForIsccPlus: z.boolean().optional(),
  hasSeparateRFNBORCF: z.boolean().optional(),
})
  .and(ZodLowILUCRisk)
  .and(ZodLowDisplacementRisk)
  .and(ZodAdditionalCertification)
  .refine((data) => {
    const dataTyped = data as Certification; // Zod cannot properly infer type for dynamic `scheme` field :/

    if (dataTyped.scheme === CertificationScheme.ISCCPLUS) {
      return data.hasIRS7210ForIsccPlus !== undefined;
    } else {
      return data.hasIRS7210ForIsccPlus === undefined;
    }
  }, { error: 'If and only if certification scheme is ISCC PLUS then accompanying IRS form 7210 field is required', path: ['hasIRS7210ForIsccPlus'] })
  .refine((data) => {
    const dataTyped = data as Certification; // Zod cannot properly infer type for dynamic `scheme` field :/

    if (dataTyped.scheme === CertificationScheme.RSBEURED || dataTyped.scheme === CertificationScheme.ISCCEU) {
      return data.hasSeparateRFNBORCF !== undefined;
    } else {
      return data.hasSeparateRFNBORCF === undefined;
    }
  }, { error: 'If and only if certification scheme is RSB EU RED or ISCC EU then RFNBO/RCF document field is required', path: ['hasSeparateRFNBORCF'] });
